import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { generateFromIdea, type Platform } from '../../lib/api';
import LanguageSelector from '../../components/LanguageSelector';

/**
 * Create content from a simple text idea
 * User enters idea text, selects platforms, and AI generates variants
 */
export default function CreateFromIdeaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const location = useLocation();

  // Get prefilled data from location state (from Ideas page)
  const prefilledIdea = location.state?.idea || '';
  const prefilledPlatforms = location.state?.platforms || [];
  const savedIdeaId = location.state?.savedIdeaId || null;

  // Form state
  const [idea, setIdea] = useState(prefilledIdea);
  const [title, setTitle] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(
    prefilledPlatforms.length > 0 ? prefilledPlatforms : ['linkedin', 'instagram']
  );
  const [language, setLanguage] = useState('en'); // Default to English, will use workspace default if available
  const [isLoading, setIsLoading] = useState(false);

  // Platform options
  const platforms: { id: Platform; label: string; icon: string }[] = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
    { id: 'facebook', label: 'Facebook', icon: '👥' },
    { id: 'twitter', label: 'Twitter', icon: '🐦' },
    { id: 'youtube', label: 'YouTube', icon: '📺' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  ];

  // Toggle platform selection
  const togglePlatform = (platformId: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((p) => p !== platformId)
        : [...prev, platformId]
    );
  };

  // Form validation
  const isValid = idea.length >= 10 && idea.length <= 5000 && selectedPlatforms.length > 0;
  const charCount = idea.length;
  const charLimit = 5000;

  // Handle form submission
  const handleGenerate = async () => {
    if (!isValid) return;

    setIsLoading(true);

    try {
      // API now returns job ID instead of post
      const { jobId } = await generateFromIdea({
        idea,
        platforms: selectedPlatforms,
        title: title || undefined,
        language: language || undefined,
        savedIdeaId: savedIdeaId || undefined,
      });

      toast.success(t('create.idea.jobStarted', { defaultValue: 'Content generation started! Redirecting to Approve Board...' }));

      // Navigate to approve board with job ID
      navigate(`/${workspaceSlug}/approve`, {
        state: { activeJobIds: [jobId] }
      });
    } catch (error: any) {
      console.error('Failed to generate content:', error);

      // Handle 429 rate limit error specifically
      if (error.response?.status === 429) {
        const { activeJobs, limit } = error.response.data;
        toast.error(
          `You have ${activeJobs} active jobs. Maximum is ${limit}. Please wait for a job to complete.`
        );
      } else {
        toast.error(
          error.response?.data?.error || t('create.idea.error')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/${workspaceSlug}/dashboard`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t('create.backToDashboard')}
        </button>

        <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">
          {t('create.idea.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t('create.idea.subtitle')}
        </p>
      </div>

      {/* Main Form */}
      <div className="space-y-6">
        {/* Idea Input */}
        <div>
          <label className="block text-sm font-bold mb-2 dark:text-gray-200">
            {t('create.idea.ideaLabel')}
          </label>
          <div className="relative">
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder={t('create.idea.ideaPlaceholder')}
              rows={8}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-100 resize-none"
            />
            <div className="absolute bottom-3 right-3 text-sm text-gray-500 dark:text-gray-400">
              {charCount} / {charLimit}
            </div>
          </div>
          {charCount < 10 && charCount > 0 && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {t('create.idea.ideaTooShort')}
            </p>
          )}
        </div>

        {/* Title (Optional) */}
        <div>
          <label className="block text-sm font-bold mb-2 dark:text-gray-200">
            {t('create.idea.titleLabel')} <span className="text-gray-500 font-normal">{t('common.optional')}</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('create.idea.titlePlaceholder')}
            className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>

        {/* Language Selection */}
        <LanguageSelector
          value={language}
          onChange={setLanguage}
          label="Content Language"
          description="Choose the language for AI-generated content"
        />

        {/* Platform Selection */}
        <div>
          <label className="block text-sm font-bold mb-3 dark:text-gray-200">
            {t('create.idea.platformsLabel')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {platforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => togglePlatform(platform.id)}
                className={`flex items-center p-4 border-2 rounded-lg transition-all ${
                  selectedPlatforms.includes(platform.id)
                    ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black'
                    : 'border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-2xl mr-3">{platform.icon}</span>
                <span className="font-medium">{platform.label}</span>
              </button>
            ))}
          </div>
          {selectedPlatforms.length === 0 && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {t('create.idea.selectPlatform')}
            </p>
          )}
        </div>

        {/* Token Cost Info */}
        <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium dark:text-gray-100">
                {t('create.idea.estimatedCost')}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('create.idea.costDescription', {
                  platforms: selectedPlatforms.length,
                  chars: charCount,
                })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold dark:text-gray-100">
                ~{Math.max(75, Math.ceil(charCount / 50) * selectedPlatforms.length * 25)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('common.tokens')}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4">
          <button
            onClick={() => navigate(`/${workspaceSlug}/dashboard`)}
            className="px-6 py-3 border-2 border-black dark:border-gray-600 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-100"
          >
            {t('common.cancel')}
          </button>

          <button
            onClick={handleGenerate}
            disabled={!isValid || isLoading}
            className={`px-8 py-3 rounded-lg font-medium transition-all ${
              isValid && !isLoading
                ? 'bg-black dark:bg-white text-white dark:text-black hover:shadow-brutal'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('create.idea.generating')}
              </span>
            ) : (
              t('create.idea.generateButton')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
